import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Pencil, Link2, Clock, MapPin, ShieldCheck, FileText, CheckCircle2, AlertCircle, Info, Plus,
} from 'lucide-react';
import { useMentorData, fmtTime } from './mentorData';
import {
  MentorWorkspace, MentorLoading, NoMentorProfile, DeclarationDialog,
} from './MentorComponents';

function Block({ title, action, children, testId }) {
  return (
    <section className="py-7 border-b border-[#EFF0F2] last:border-0" data-testid={testId}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="text-base md:text-lg font-bold text-[#1F2937] font-['Outfit'] tracking-tight">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{label}</p>
      <p className="text-sm text-[#1F2937] mt-1">{value || '—'}</p>
    </div>
  );
}

export default function MentorProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { loading, profile, eligibility, declarationInfo, refresh } = useMentorData();
  const [showDeclaration, setShowDeclaration] = useState(false);

  if (loading) return <MentorLoading />;
  if (!profile) return <NoMentorProfile />;

  const st = profile.approval_status;
  const isApproved = st === 'approved';
  const canEdit = ['draft', 'rejected', 'pending'].includes(st) || isApproved;
  const needsDeclaration = declarationInfo && !declarationInfo.has_accepted;
  const suitabilityPending = (eligibility?.outstanding_requirements || []).some(r => r.toLowerCase().includes('suitability'));

  const statusBadge = {
    approved: 'bg-emerald-50 text-emerald-600', pending: 'bg-amber-50 text-amber-700',
    suspended: 'bg-orange-50 text-orange-700', deactivated: 'bg-slate-100 text-slate-600',
    rejected: 'bg-red-50 text-red-600', draft: 'bg-gray-100 text-gray-500',
  }[st] || 'bg-gray-100 text-gray-500';

  return (
    <MentorWorkspace testId="mentor-profile-page">
      {/* Identity header */}
      <div className="flex items-start gap-4 sm:gap-5 mb-2">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#F97D6A] to-[#F9A88C] flex items-center justify-center shrink-0">
          <span className="text-2xl sm:text-3xl font-bold text-white">
            {((user?.full_name || 'M').replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Prof\.?)\s+/i, '')[0] || 'M').toUpperCase()}
          </span>
        </div>
        <div className="min-w-0 pt-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight truncate">{user?.full_name}</h1>
          <p className="text-sm sm:text-base text-[#6B7280] mt-1">{profile.profession_title}{profile.company_name ? ` at ${profile.company_name}` : ''}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={`border-0 text-[10px] ${statusBadge}`} data-testid="profile-status-badge">{st}</Badge>
            {profile.has_pending_changes && <Badge className="border-0 text-[10px] bg-blue-50 text-blue-600" data-testid="profile-pending-badge">Update pending review</Badge>}
          </div>
        </div>
      </div>

      {profile.has_pending_changes && (
        <div className="rounded-2xl bg-blue-50 px-5 py-4 mt-6 flex items-start gap-3" data-testid="pending-changes-notice">
          <FileText className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-800">Profile update pending review</p>
            <p className="text-xs text-blue-600 mt-0.5">Your proposed professional changes are with your Career Coordinator. The approved profile below stays active until they review it.</p>
          </div>
        </div>
      )}

      <div className="mt-4">
        {/* Professional information */}
        <Block
          title="Professional Information"
          testId="professional-information"
          action={canEdit && (
            <Button variant="outline" size="sm" onClick={() => navigate('/mentor/onboarding')} className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB] shrink-0" data-testid="edit-profile-btn">
              <Pencil className="h-3 w-3 mr-1.5" /> Edit Profile
            </Button>
          )}
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name" value={user?.full_name} />
            <Field label="Profession" value={profile.profession_title} />
            <Field label="Company" value={profile.company_name} />
            <Field label="Experience" value={profile.years_of_experience ? `${profile.years_of_experience} years` : null} />
          </div>
          {profile.bio && (
            <div className="mt-5">
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1.5">Bio</p>
              <p className="text-sm text-[#4B5563] leading-relaxed max-w-2xl">{profile.bio}</p>
            </div>
          )}
          {profile.linkedin_url && (
            <a href={profile.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#F97D6A] hover:underline mt-5" data-testid="linkedin-link">
              <Link2 className="h-3.5 w-3.5" /> LinkedIn Profile
            </a>
          )}
        </Block>

        {/* Career expertise */}
        <Block title="Career Expertise" testId="career-expertise">
          <p className="text-xs text-[#9CA3AF] mb-3">Careers your school has approved you to mentor.</p>
          <div className="flex flex-wrap gap-2" data-testid="mapped-careers-list">
            {(profile.mapped_careers || []).map(c => (
              <Badge key={c.slug} className="bg-[#FFF0ED] text-[#F97D6A] border-0 rounded-full px-3 py-1 text-xs font-semibold">{c.name}</Badge>
            ))}
            {(!profile.mapped_careers || profile.mapped_careers.length === 0) && <span className="text-sm text-[#9CA3AF]">No careers mapped yet</span>}
          </div>
          {profile.notes_for_coordinator && (
            <div className="mt-5">
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1.5">Additional Notes</p>
              <p className="text-sm text-[#4B5563] leading-relaxed max-w-2xl">{profile.notes_for_coordinator}</p>
            </div>
          )}
        </Block>

        {/* Availability */}
        <Block
          title="Availability"
          testId="availability-block"
          action={canEdit && (
            <Button variant="outline" size="sm" onClick={() => navigate('/mentor/onboarding')} className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB] shrink-0" data-testid="edit-availability-btn">
              <Pencil className="h-3 w-3 mr-1.5" /> Edit Availability
            </Button>
          )}
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-blue-500" />
                <p className="text-sm font-semibold text-[#1F2937]">Online weekly availability</p>
              </div>
              {(profile.availability_config || []).length === 0 ? (
                <p className="text-sm text-[#9CA3AF]">Not configured</p>
              ) : (
                <div className="space-y-2">
                  {profile.availability_config.map((day, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-[#1F2937] w-10">{day.day?.slice(0, 3)}</span>
                      <div className="flex flex-wrap gap-1">
                        {(day.time_ranges || []).map((tr, j) => (
                          <span key={j} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-[10px]">{fmtTime(tr.start)} – {fmtTime(tr.end)}{tr.duration_minutes ? ` · ${tr.duration_minutes}m` : ''}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-emerald-500" />
                <p className="text-sm font-semibold text-[#1F2937]">Workplace Visit Offerings</p>
              </div>
              {(profile.offline_capabilities || []).length === 0 ? (
                <p className="text-sm text-[#9CA3AF] mb-3">No workplace opportunities published</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {profile.offline_capabilities.map((cap, i) => {
                    const careerName = (profile.mapped_careers || []).find(c => c.slug === cap.career_slug)?.name || cap.career_slug;
                    return (
                      <div key={i} className="text-xs">
                        <p className="font-semibold text-[#1F2937]">{careerName}</p>
                        <p className="text-[11px] text-[#6B7280]">{cap.workplace_name} · {cap.city}{cap.date ? ` · ${formatDate(cap.date)}` : ''}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate('/mentor/experiences?tab=offline')} className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB]" data-testid="manage-offline-btn">
                <Plus className="h-3 w-3 mr-1.5" /> Manage Workplace Visits
              </Button>
            </div>
          </div>
        </Block>

        {/* Safeguarding & approval */}
        <Block title="Safeguarding & Approval" testId="safeguarding-approval">
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-2xl border border-[#EFF0F2] p-4" data-testid="declaration-status">
              {needsDeclaration ? <AlertCircle className="h-5 w-5 text-[#F97D6A] shrink-0" /> : <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1F2937]">Safeguarding declaration</p>
                <p className="text-sm text-[#6B7280] mt-0.5">
                  {needsDeclaration
                    ? `Version ${declarationInfo?.declaration_version} needs your acceptance.`
                    : `Accepted${declarationInfo?.accepted_at ? ` on ${formatDate(declarationInfo.accepted_at)}` : ''}${declarationInfo?.accepted_version ? ` (v${declarationInfo.accepted_version})` : ''}`}
                </p>
              </div>
              {needsDeclaration && (
                <Button size="sm" onClick={() => setShowDeclaration(true)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-8 px-3.5 text-xs font-semibold shrink-0" data-testid="review-accept-btn">
                  Review & Accept
                </Button>
              )}
            </div>

            <div className="flex items-center gap-3 rounded-2xl border border-[#EFF0F2] p-4" data-testid="approval-status">
              {isApproved ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1F2937]">Mentor approval</p>
                <p className="text-sm text-[#6B7280] mt-0.5">
                  {st === 'approved' ? 'Approved by your Career Coordinator' : st === 'pending' ? 'Application under review' : <span className="capitalize">{st}</span>}
                  {profile.suspension_reason ? ` — ${profile.suspension_reason}` : ''}
                </p>
              </div>
            </div>

            {suitabilityPending && (
              <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4" data-testid="suitability-info-notice">
                <Info className="h-5 w-5 text-slate-500 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">School verification pending</p>
                  <p className="text-xs text-slate-500 mt-0.5">Your coordinator completes the school suitability check — no action needed from you.</p>
                </div>
              </div>
            )}
          </div>
        </Block>
      </div>

      <DeclarationDialog open={showDeclaration} onOpenChange={setShowDeclaration} version={declarationInfo?.declaration_version} onDone={refresh} />
    </MentorWorkspace>
  );
}
